/**
 * In-note component kits.
 *
 * A kit is a named set of fence renderers the webapp already has.
 * The note lists which kits it imports. A fence whose kit is not imported
 * stays source. Core ships with every note. Review is optional.
 *
 * Access: http://flownotes.localhost/?test=structured-doc-blocks
 * The Jan 2026 /?test=folder-dashboard prototype is a widget grid, a different job.
 */

import { useMemo, useRef, useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import videoSeriesLinks from '@/components/structured-doc-specimens/video-series-links.md?raw'
import speedyStaticCaching from '@/components/structured-doc-specimens/speedy-static-caching.md?raw'
import twilioError21612 from '@/components/structured-doc-specimens/twilio-error-21612.md?raw'
import { Switch } from '@/components/ui/switch'
import { testIds } from '@/lib/test-ids'
import { useTheme } from '@/providers/theme-provider'
import {
  CHECKLIST,
  documentSource,
} from '@/components/test-structured-doc-blocks.data'
import {
  StructuredDocMarkdown,
  toggleChecklistItem,
} from '@/components/test-structured-doc-blocks.markdown'
import '@/components/features/document-reading-prose.css'
import '@/components/test-structured-doc-blocks.css'

type View = 'preview' | 'source'
type SpecimenId = 'soul' | 'video' | 'speedy' | 'twilio'

const SPECIMENS: Array<{ id: SpecimenId; label: string; markdown?: string; note: string }> = [
  { id: 'soul', label: 'Soul audit (kit)', note: 'Kit demo. Callout, filterable table, pros and cons, checklist, properties.' },
  {
    id: 'video',
    label: 'Video series links',
    markdown: videoSeriesLinks,
    note: 'Bug work spec. Greater Good, Aug 28. Core and review kits.',
  },
  {
    id: 'speedy',
    label: 'Speedy static cache',
    markdown: speedyStaticCaching,
    note: 'Estimate work spec. Decor Rest, 6-10h. Core and review kits.',
  },
  {
    id: 'twilio',
    label: 'Twilio 21612',
    markdown: twilioError21612,
    note: 'Investigation work spec. GGIA, Aug 21. Core and review kits.',
  },
]

export function StructuredDocBlocksTest() {
  const { resolvedTheme, setTheme } = useTheme()
  const [view, setView] = useState<View>('preview')
  const [specimenId, setSpecimenId] = useState<SpecimenId>('video')
  const [review, setReview] = useState(true)
  const [done, setDone] = useState<boolean[]>(() => CHECKLIST.map(() => false))
  const [drafts, setDrafts] = useState<Partial<Record<SpecimenId, string>>>({})
  const scrollerRef = useRef<HTMLDivElement>(null)

  const specimen = SPECIMENS.find((item) => item.id === specimenId) ?? SPECIMENS[0]
  const showingSoul = specimen.id === 'soul'
  const specMarkdown = specimen.markdown ? (drafts[specimen.id] ?? specimen.markdown) : ''
  const soulSource = useMemo(() => documentSource(review, done), [review, done])
  const source = showingSoul ? soulSource : specMarkdown

  useEffect(() => {
    scrollerRef.current?.scrollTo({ top: 0 })
  }, [view, review, specimenId])

  function onChecklistToggle(fenceIndex: number, itemIndex: number) {
    if (showingSoul) {
      if (fenceIndex !== 0) return
      setDone((current) => current.map((value, index) => (index === itemIndex ? !value : value)))
      return
    }
    setDrafts((current) => {
      const base = current[specimen.id] ?? specimen.markdown ?? ''
      return { ...current, [specimen.id]: toggleChecklistItem(base, fenceIndex, itemIndex) }
    })
  }

  return (
    <div
      className="flex h-screen flex-col bg-background text-foreground"
      data-testid={testIds.structuredDocBlocks.root}
    >
      <header className="flex h-12 shrink-0 items-center gap-3 border-b px-4">
        <div className="min-w-0">
          <div className="text-sm font-medium">Structured doc kits</div>
        </div>
        <label className="min-w-0 text-xs text-muted-foreground" htmlFor="sdoc-specimen">
          <span className="sr-only">Document</span>
          <select
            id="sdoc-specimen"
            className="max-w-[16rem] truncate rounded-md border border-border bg-background px-2 py-1 text-xs text-foreground"
            value={specimenId}
            data-testid={testIds.structuredDocBlocks.specimen}
            onChange={(event) => setSpecimenId(event.target.value as SpecimenId)}
          >
            {SPECIMENS.map((item) => (
              <option key={item.id} value={item.id}>{item.label}</option>
            ))}
          </select>
        </label>
        <div className="ml-auto flex items-center gap-3">
          {showingSoul ? (
          <label className="flex items-center gap-2 text-xs" htmlFor="sdoc-import-review">
            Import review kit
            <Switch
              id="sdoc-import-review"
              checked={review}
              onCheckedChange={setReview}
              data-testid={testIds.structuredDocBlocks.importReview}
            />
          </label>
          ) : null}
          <div className="sdoc-segmented" style={{ margin: 0 }}>
            <button
              type="button"
              aria-pressed={view === 'preview'}
              data-testid={testIds.structuredDocBlocks.preview}
              onClick={() => setView('preview')}
            >
              Preview
            </button>
            <button
              type="button"
              aria-pressed={view === 'source'}
              data-testid={testIds.structuredDocBlocks.source}
              onClick={() => setView('source')}
            >
              Source
            </button>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => void setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
          >
            {resolvedTheme === 'dark' ? 'Light' : 'Dark'}
          </Button>
        </div>
      </header>

      <div ref={scrollerRef} className="min-h-0 flex-1 overflow-auto">
        {view === 'source' ? (
          <pre className="sdoc-source mx-auto box-border w-full max-w-4xl px-8 py-8 text-foreground">{source}</pre>
        ) : (
          <article
            className="document-reading-prose mx-auto box-border w-full max-w-4xl px-8 py-8"
            data-testid={testIds.structuredDocBlocks.article}
          >
            <p className="text-xs text-muted-foreground">{specimen.note}</p>
            <StructuredDocMarkdown
              source={source}
              resetKey={specimenId}
              onChecklistToggle={onChecklistToggle}
            />
          </article>
        )}
      </div>
    </div>
  )
}
